/**
 * Отображения между заказом REST и корзиной виджета.
 *
 * REST отдаёт позиции СГРУППИРОВАННЫМИ: две одинаковые позиции — одна группа
 * с quantity: 2. Идентификатор группы — не uuid, а непрозрачная строка, и
 * именно её принимают itemsUpdate и itemsRemove мутации обновления.
 */

import type { OrderData } from "@/types/iframe-messages";
import type { OrderItem, CurrentOrderState } from "./types";
import type {
  RestOrder,
  RestOrderItemGroup,
  OrderItemAdd,
  OrderItemGroupUpdate,
} from "./schemaTypes";
import { itemKey } from "@/core/options/fingerprint";

export function groupToItem(group: RestOrderItemGroup): OrderItem {
  const s = group.productSnapshot;
  return {
    id: group.id,
    productId: s.productId,
    quantity: group.quantity,
    // group.price включает опции; в позиции корзины price — цена товара,
    // а опции прибавляет itemPrice. Иначе они посчитались бы дважды.
    price: group.price - (group.options ?? []).reduce(
      (sum, o) => sum + o.price * o.quantity, 0,
    ),
    name: s.name,
    productSnapshot: {
      id: s.id,
      productId: s.productId,
      name: s.name,
      imageId: s.imageId,
      variantProperties: s.variantProperties ?? [],
      categories: s.categories,
    },
    options: (group.options ?? []).map((o) => ({
      blockId: o.blockId,
      optionId: o.optionId,
      quantity: o.quantity,
      name: o.optionName,
      blockName: o.blockName,
      groupName: o.optionGroupName,
      price: o.price,
    })),
  };
}

/**
 * Позиция корзины → тело `itemsAdd`.
 *
 * Ключ options опускаем, а не шлём пустым массивом: контракт считает их
 * равнозначными, а тело без лишнего ключа читается однозначнее. Опция несёт
 * дальше только то, что примет мутация (blockId, optionId, quantity) —
 * имена и цена в теле не нужны, их знает сам сервер.
 */
function toItemAdd(item: OrderItem): OrderItemAdd {
  const add: OrderItemAdd = {
    productId: item.productSnapshot.productId || item.productId,
    quantity: item.quantity,
  };
  if (item.options.length > 0) {
    add.options = item.options.map(({ blockId, optionId, quantity }) => ({
      blockId,
      optionId,
      quantity,
    }));
  }
  return add;
}

/** Корзина → позиции тела создания. Сервер принимает только productId, quantity и options. */
export function cartToItemsAdd(cart: CurrentOrderState): OrderItemAdd[] {
  return cart.items.map(toItemAdd);
}

export interface OrderPatchDelta {
  itemsAdd: OrderItemAdd[];
  itemsUpdate: OrderItemGroupUpdate[];
  itemsRemove: string[];
}

/**
 * Дельта «состав сервера → состав корзины».
 *
 * Сопоставление по productId: в REST вариант товара — самостоятельный
 * productId. Адресация правок — только идентификатором группы: productId
 * сервер в itemsUpdate не примет (13213, OrderItemNotFoundError).
 *
 * Полная замена состава (`items`) не используется: способ правки в коде один,
 * и слать весь состав на каждое нажатие «+» незачем. Снятие всегда через
 * itemsRemove, хотя контракт допускает и quantity: 0 — одно правило дешевле.
 *
 * Ключ позиции — товар ПЛЮС набор опций: набор у существующей группы
 * контрактом не правится (OrderItemGroupUpdate = {id, quantity}), поэтому
 * смена выбора обязана выразиться снятием и добавлением. Она и выражается
 * сама: старый ключ пропал из корзины, новый появился.
 */
export function diffToPatch(
  serverItems: readonly OrderItem[],
  cartItems: readonly OrderItem[]
): OrderPatchDelta {
  const server = new Map(serverItems.map((i) => [itemKey(i), i]));
  const cart = new Map(cartItems.map((i) => [itemKey(i), i]));

  const itemsAdd: OrderItemAdd[] = [];
  const itemsUpdate: OrderItemGroupUpdate[] = [];
  const itemsRemove: string[] = [];

  for (const [key, item] of cart) {
    const onServer = server.get(key);
    if (!onServer) {
      itemsAdd.push(toItemAdd(item));
    } else if (onServer.quantity !== item.quantity && onServer.id) {
      itemsUpdate.push({ id: onServer.id, quantity: item.quantity });
    }
  }

  for (const [key, item] of server) {
    if (!cart.has(key) && item.id) itemsRemove.push(item.id);
  }

  return { itemsAdd, itemsUpdate, itemsRemove };
}

export function isEmptyDelta(d: OrderPatchDelta): boolean {
  return (
    d.itemsAdd.length === 0 &&
    d.itemsUpdate.length === 0 &&
    d.itemsRemove.length === 0
  );
}

/**
 * Тип заказа REST → словарь iframe.
 *
 * Пишется заново, а не переносится из utils.ts: прежний convertOrderType
 * сравнивал со string-энумом OrderType легаси-схемы, а такое сравнение с
 * юнионом не компилируется. ON_TABLE наружу уходит как PREORDER — своего
 * случая в протоколе SDK у него нет.
 */
function orderTypeToIframe(type: RestOrder["type"]): OrderData["type"] {
  switch (type) {
    case "PRE_ORDER":
    case "ON_TABLE":
      return "PREORDER";
    case "DELIVERY":
      return "DELIVERY";
    case "PICKUP":
      return "PICKUP";
    default: {
      const _exhaustive: never = type;
      throw new Error(`Неизвестный тип заказа: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Заказ REST → полезная нагрузка сообщений хосту.
 *
 * Второго аргумента нет: brandId, pointId и comment несёт сам заказ, и брать
 * их из стора значило бы отправить хосту бренд, отличный от бренда заказа,
 * после EDIT_ORDER чужой точки.
 */
export function orderToOrderData(order: RestOrder): OrderData {
  return {
    orderId: order.id,
    items: order.items.map((group) => ({
      uuid: group.id,
      name: group.productSnapshot.name,
      quantity: group.quantity,
      price: group.price,
    })),
    priceTotal: order.priceTotal,
    comment: order.comment ?? undefined,
    brandId: order.brandId,
    pointId: order.pointId,
    timestamp: Date.now(),
    type: orderTypeToIframe(order.type),
  };
}
